// Device, display and local-clock context, sampled at capture time.
//
// These answer "what was the user looking at, and what did their machine think
// the time was" — the questions a support ticket opens with. `client.captured_at`
// paired with Sentry's own event timestamp and receipt time (both automatic) is
// what makes a misconfigured device clock visible instead of mysterious.
//
// None of this is identifying: a device *class* and a viewport size, never a
// machine name, model or serial. And none of it participates in fingerprinting —
// two users hitting the same broken endpoint from different screens must land in
// the same issue.

export type DeviceType = 'desktop' | 'mobile' | 'tablet';

const TABLET_PATTERN = /tablet|ipad|playbook|silk|(android(?!.*mobi))/i;
const MOBILE_PATTERN = /mobi|iphone|ipod|android|blackberry|opera mini|iemobile/i;

const pad = (value: number, length = 2): string =>
  String(Math.abs(value)).padStart(length, '0');

/** Device class from the user agent. Falls back to `desktop` when unrecognizable. */
export const getDeviceType = (userAgent: string): DeviceType => {
  if (TABLET_PATTERN.test(userAgent)) {
    return 'tablet';
  }
  return MOBILE_PATTERN.test(userAgent) ? 'mobile' : 'desktop';
};

/**
 * ISO-8601 timestamp in the device's *local* time, including its UTC offset
 * (`2026-08-10T12:30:00.000+02:00`). Deliberately not `toISOString()`, which
 * normalizes to UTC and would erase the very thing being reported.
 */
export const toLocalIsoString = (date: Date): string => {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const offset = `${sign}${pad(Math.trunc(offsetMinutes / 60))}:${pad(offsetMinutes % 60)}`;

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `.${pad(date.getMilliseconds(), 3)}${offset}`
  );
};

const getTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // `Intl` is unavailable or the environment has no resolvable zone.
    return undefined;
  }
};

export interface ClientContext {
  // Low-cardinality, searchable facets.
  tags: Record<string, string>;
  // `captured_at` is millisecond-precise, so its value is unique per event. A tag
  // whose value never repeats is useless as a search facet and only inflates
  // Sentry's tag index, so it goes on a context instead — still visible next to
  // the event timestamp and Sentry's receipt time, which is all the clock-skew
  // comparison needs.
  context: { captured_at: string };
}

/** Client device and local-clock context, sampled now. */
export const getClientContext = (): ClientContext => {
  const now = new Date();
  const tags: Record<string, string> = {
    'client.utc_offset_minutes': String(-now.getTimezoneOffset()),
  };

  const timeZone = getTimeZone();
  if (timeZone) {
    tags['client.time_zone'] = timeZone;
  }

  if (typeof navigator !== 'undefined') {
    if (navigator.language) {
      tags['client.locale'] = navigator.language;
    }
    tags['client.device_type'] = getDeviceType(navigator.userAgent ?? '');
  }

  if (typeof window !== 'undefined') {
    const { screen, innerWidth, innerHeight } = window;
    if (screen) {
      tags['client.screen_resolution'] = `${screen.width}x${screen.height}`;
    }
    tags['client.viewport'] = `${innerWidth}x${innerHeight}`;
  }

  return { tags, context: { captured_at: toLocalIsoString(now) } };
};
